import { useEffect, useState, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TrialsTab from "@/components/TrialsTab";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Save, Upload, BookOpen, Building2, CreditCard, Trash2, CheckCircle2, Send, RefreshCw, AlertTriangle, Download, Loader2, Database, Play, RotateCcw, ExternalLink, Mail, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const ESTADO_COLOR = { trial: "bg-[#0E2A47]", activo: "bg-[#10B981]", expirado: "bg-[#EF4444]" };

export default function Admin() {
  const [doctrina, setDoctrina] = useState([]);
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState({ titulo: "", contenido: "" });
  const [jsonText, setJsonText] = useState("");
  const [modo, setModo] = useState("upsert");
  const [result, setResult] = useState(null);
  const [reqCount, setReqCount] = useState(0);
  const [impresiones, setImpresiones] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [planEdit, setPlanEdit] = useState({});
  const [activarSel, setActivarSel] = useState({});
  const [borrar, setBorrar] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [legales, setLegales] = useState([]);
  const [legalEdit, setLegalEdit] = useState({});
  const [webhook, setWebhook] = useState(null);
  const [whLoading, setWhLoading] = useState(false);
  const [backupData, setBackupData] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreRes, setRestoreRes] = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [descartarBusy, setDescartarBusy] = useState(false);
  const [descartarRes, setDescartarRes] = useState(null);
  const [confirmDescartar, setConfirmDescartar] = useState(false);
  const [migDry, setMigDry] = useState(null);
  const [migApplied, setMigApplied] = useState(null);
  const [migBusy, setMigBusy] = useState(false);
  const [confirmMig, setConfirmMig] = useState(false);
  const [bfDry, setBfDry] = useState(null);
  const [bfBusy, setBfBusy] = useState(false);
  const [bfReanalizar, setBfReanalizar] = useState(false);
  const [huerf, setHuerf] = useState(null);
  const [huerfBusy, setHuerfBusy] = useState(false);
  const [mrej, setMrej] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyTxt, setApplyTxt] = useState("");
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyRes, setApplyRes] = useState(null);
  const [kdry, setKdry] = useState(null);
  const [kdryBusy, setKdryBusy] = useState(false);
  const [obClics, setObClics] = useState(null);
  const [react, setReact] = useState(null);
  const [reactBusy, setReactBusy] = useState(false);
  const [reactConf, setReactConf] = useState("");
  const [reactRes, setReactRes] = useState(null);
  const [aperturas, setAperturas] = useState(null);
  const [apBusy, setApBusy] = useState(false);
  const [reservas, setReservas] = useState(null);
  const [reservasBusy, setReservasBusy] = useState(false);
  const [trunc, setTrunc] = useState(null);
  const [truncBusy, setTruncBusy] = useState(false);
  const [reanDry, setReanDry] = useState(null);
  const [reanTenant, setReanTenant] = useState(null);
  const [reanBusy, setReanBusy] = useState(false);
  const cargarTruncamiento = async () => {
    setTruncBusy(true);
    try { const r = await api.get("/documentos/diagnostico-truncamiento"); setTrunc(r.data); }
    catch (e) { toast.error("No se pudo ejecutar la auditoría"); }
    finally { setTruncBusy(false); }
  };
  const reanTenantDry = async (tid) => {
    setReanBusy(true); setReanTenant(tid); setReanDry(null);
    try { const r = await api.post(`/documentos/reanalizar-tenant-dryrun?tenant_id=${tid}`); setReanDry(r.data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); setReanTenant(null); }
    finally { setReanBusy(false); }
  };
  const reanTenantAplicar = async () => {
    if (!reanTenant) return;
    setReanBusy(true);
    try { const r = await api.post(`/documentos/reanalizar-tenant-aplicar?tenant_id=${reanTenant}`);
      toast.success(`Reanalizados ${r.data.reanalizados}/${r.data.total} documentos`); setReanDry(null); setReanTenant(null); cargarTruncamiento(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setReanBusy(false); }
  };
  const cargarAperturas = async () => {
    setApBusy(true);
    try { const r = await api.get("/admin/reactivacion/aperturas"); setAperturas(r.data); }
    catch (e) { toast.error("No se pudieron cargar las aperturas"); }
    finally { setApBusy(false); }
  };

  const cargarReservas = async () => {
    setReservasBusy(true);
    try { const r = await api.get("/admin/reservas"); setReservas(r.data); }
    catch (e) { toast.error("No se pudieron cargar las reservas"); }
    finally { setReservasBusy(false); }
  };

  const cargarReact = async () => {
    setReactBusy(true); setReactRes(null);
    try { const r = await api.get("/admin/reactivacion/preview"); setReact(r.data); }
    catch (e) { toast.error("No se pudo cargar la vista previa"); }
    finally { setReactBusy(false); }
  };
  const enviarReact = async () => {
    setReactBusy(true);
    try { const r = await api.post("/admin/reactivacion/enviar", { confirmacion: reactConf }); setReactRes(r.data); setReactConf(""); await cargarReact(); toast.success(`Enviados: ${r.data.enviados} · Fallos: ${r.data.fallidos}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
    finally { setReactBusy(false); }
  };

  const kpisDryRun = async () => {
    setKdryBusy(true);
    try {
      const { data } = await api.get("/migracion/kpis-dryrun");
      setKdry(data);
      toast.success("Dry-run de KPIs listo");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setKdryBusy(false);
  };

  const aplicarBackfill = async () => {
    setApplyBusy(true);
    try {
      const { data } = await api.post("/migracion/backfill-avisos-ia-aplicar?confirmar=true");
      setApplyRes(data);
      setApplyOpen(false); setApplyTxt("");
      toast.success(`Backfill aplicado: ${data.docs_actualizados} docs · backup ${data.backup_coleccion}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setApplyBusy(false);
  };

  const auditarHuerfanos = async () => {
    setHuerfBusy(true);
    try {
      const { data } = await api.get("/migracion/auditar-huerfanos");
      setHuerf(data);
      try { const { data: m } = await api.get("/migracion/mapeos-rechazados"); setMrej(m); } catch { /* noop */ }
      toast.success(`Auditoría: ${data.tenants_reales_afectados} tenant(s) real(es) afectado(s)`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setHuerfBusy(false);
  };

  const backfillDryRun = async () => {
    setBfBusy(true);
    try {
      const { data } = await api.post(`/migracion/backfill-avisos-ia?reanalizar=${bfReanalizar}`);
      setBfDry(data);
      toast.success(`Dry-run: ${data.tenants_con_impacto} tenants con impacto`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setBfBusy(false);
  };

  const migracionDryRun = async () => {
    setMigBusy(true); setMigApplied(null);
    try {
      const { data } = await api.post("/migracion/scoring-ia-vigente");
      setMigDry(data);
      toast.success(`Dry-run: ${data.tenants} tenants analizados`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setMigBusy(false);
  };

  const migracionAplicar = async () => {
    setMigBusy(true); setConfirmMig(false);
    try {
      const { data } = await api.post("/migracion/scoring-ia-vigente?apply=true");
      setMigApplied(data);
      setMigDry(null);
      toast.success(`Migración aplicada: ${data.total_docs_migrados} docs · ${data.total_gaps_cerrados} gaps cerrados`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setMigBusy(false);
  };

  const comprobarIntegraciones = async () => {
    setDiagBusy(true); setDescartarRes(null);
    try { const { data } = await api.get("/admin/diagnostico/integraciones"); setDiag(data); toast.success("Diagnóstico actualizado"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setDiagBusy(false); }
  };

  const pendientes = diag ? ((diag.emails_ciclo_por_estado?.pendiente || 0) + (diag.emails_ciclo_por_estado?.omitido_preview || 0)) : null;

  const descartarPendientes = async () => {
    setDescartarBusy(true);
    try {
      const { data } = await api.post("/admin/diagnostico/descartar-pendientes");
      setDescartarRes(data); setConfirmDescartar(false);
      toast.success(`Descartados ${data.pendientes_descartados} · ${data.tombstones_creados} tombstones`);
      await comprobarIntegraciones();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setDescartarBusy(false); }
  };

  const cargarBackups = useCallback(async () => {
    try { const { data } = await api.get("/admin/backups"); setBackupData(data); }
    catch (e) { console.error("Error cargando estado de backups", e); setBackupData(null); }
  }, []);
  useEffect(() => { cargarBackups(); }, [cargarBackups]);

  const ejecutarBackup = async () => {
    setBackupBusy(true);
    try {
      const { data } = await api.post("/admin/backups/run");
      if (data.estado === "no_configurado") { toast.error("Backups no configurados (faltan credenciales de Dropbox)."); setBackupBusy(false); return; }
      toast.success("Backup en curso…");
      let n = 0; const iv = setInterval(async () => { n++; await cargarBackups(); if (n >= 15) { clearInterval(iv); setBackupBusy(false); } }, 3000);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setBackupBusy(false); }
  };

  const probarRestore = async () => {
    setRestoreBusy(true); setRestoreRes(null);
    try { const { data } = await api.post("/admin/backups/restore-test"); setRestoreRes(data); if (data.estado === "ok") toast.success("Restauración de prueba correcta"); else toast.error(data.mensaje || data.estado); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setRestoreBusy(false); }
  };
  const [exportDlg, setExportDlg] = useState(null);
  const [exportaciones, setExportaciones] = useState([]);
  const [exportBusy, setExportBusy] = useState(false);

  const cargarExportaciones = async (tid) => {
    try { const { data } = await api.get(`/admin/export/${tid}`); setExportaciones(data); }
    catch (e) { console.error("Error cargando exportaciones del tenant", e); setExportaciones([]); }
  };
  const abrirExport = (t) => { setExportDlg(t); setExportaciones([]); cargarExportaciones(t.id); };
  const generarExport = async () => {
    const tid = exportDlg.id; setExportBusy(true);
    try {
      await api.post(`/admin/export/${tid}`);
      toast.success("Exportación en curso… se generará en segundo plano.");
      await cargarExportaciones(tid);
      let n = 0; const iv = setInterval(async () => { n++; await cargarExportaciones(tid); if (n >= 12) clearInterval(iv); }, 3000);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setExportBusy(false); }
  };
  const descargarExport = async (exp) => {
    const tid = exportDlg.id;
    try {
      const { data } = await api.post(`/descargas/token?tenant_id=${tid}`, { recurso_tipo: "exportacion_tenant", recurso_id: exp.id });
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/descargas/${data.token}`, "_blank");
      setTimeout(() => cargarExportaciones(tid), 2000);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const cargarWebhook = useCallback(async () => {
    setWhLoading(true);
    try { const { data } = await api.get("/telegram/webhook-info"); setWebhook(data); }
    catch (e) { console.error("Error cargando estado del webhook de Telegram", e); setWebhook(null); }
    finally { setWhLoading(false); }
  }, []);
  useEffect(() => { cargarWebhook(); }, [cargarWebhook]);

  const reregistrarWebhook = async () => {
    try {
      const { data } = await api.post("/telegram/reparar-webhook");
      toast.success(data.coincide ? "Webhook re-registrado y verificado ✓" : "Webhook re-registrado");
      cargarWebhook();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const load = useCallback(async () => {
    const [d, r] = await Promise.all([api.get("/admin/doctrina"), api.get("/admin/requisitos")]);
    setDoctrina(d.data);
    setReqCount(r.data.filter((x) => !x.retirado).length);
    try { const imp = await api.get("/etiquetas/impresiones"); setImpresiones(imp.data); } catch { setImpresiones([]); }
    try { const [t, p, l] = await Promise.all([api.get("/tenants"), api.get("/admin/planes"), api.get("/admin/legal")]); setTenants(t.data); setPlanes(p.data); setLegales(l.data); } catch (e) { console.error("Error cargando datos de superadmin (tenants/planes/legal)", e); }
    try { const ob = await api.get("/admin/onboarding-clics"); setObClics(ob.data); } catch { setObClics(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const guardarLegal = async (clave, extra = {}) => {
    const e = legalEdit[clave] || {};
    try {
      await api.put(`/admin/legal/${clave}`, { titulo: e.titulo, contenido: e.contenido, version: e.version, ...extra });
      toast.success("Página legal guardada"); setLegalEdit((p) => ({ ...p, [clave]: undefined })); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const guardarPlan = async (codigo) => {
    const e = planEdit[codigo];
    if (!e) return;
    try {
      await api.put(`/admin/planes/${codigo}`, {
        precio: parseFloat(e.precio), features: e.features.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      toast.success(`Plan ${codigo} actualizado`); setPlanEdit((p) => ({ ...p, [codigo]: undefined })); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const activarTenant = async (tid) => {
    const plan = activarSel[tid];
    if (!plan) return toast.error("Selecciona un plan");
    try { await api.post(`/admin/tenants/${tid}/activar`, { plan_codigo: plan }); toast.success("Plan activado"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const eliminarTenant = async () => {
    try {
      await api.delete(`/admin/tenants/${borrar.id}`, { params: { confirmacion: confirmName } });
      toast.success("Empresa eliminada"); setBorrar(null); setConfirmName(""); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const abrir = (dd) => { setSel(dd.clave); setEdit({ titulo: dd.titulo, contenido: dd.contenido }); };
  const guardar = async () => {
    try {
      await api.put(`/admin/doctrina/${sel}`, edit);
      toast.success("Doctrina actualizada"); setSel(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const importar = async () => {
    let payload;
    try { payload = JSON.parse(jsonText); }
    catch { return toast.error("JSON no válido"); }
    try {
      const { data } = await api.post(`/admin/requisitos/import?modo=${modo}`, payload);
      setResult(data); toast.success(`Importados ${data.importados} · activos ${data.activos} · retirados ${data.retirados}`); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-page">
      <PageHeader overline="Superadmin · Agentik" title="Administración" />
      <Tabs defaultValue="doctrina">
        <TabsList className="bg-white border border-[#E2E8F0] flex w-full max-w-full justify-start overflow-x-auto flex-nowrap h-auto py-1 [-webkit-overflow-scrolling:touch]">
          <TabsTrigger value="doctrina" data-testid="tab-doctrina" className="shrink-0">Doctrina ({doctrina.length})</TabsTrigger>
          <TabsTrigger value="catalogo" data-testid="tab-catalogo" className="shrink-0">Catálogo ({reqCount})</TabsTrigger>
          <TabsTrigger value="etiquetas" data-testid="tab-etiquetas-admin" className="shrink-0">Etiquetas ({impresiones.length})</TabsTrigger>
          <TabsTrigger value="empresas" data-testid="tab-empresas" className="shrink-0">Empresas ({tenants.length})</TabsTrigger>
          <TabsTrigger value="trials" data-testid="tab-trials" className="shrink-0">Trials</TabsTrigger>
          <TabsTrigger value="planes" data-testid="tab-planes" className="shrink-0">Planes ({planes.length})</TabsTrigger>
          <TabsTrigger value="legales" data-testid="tab-legales" className="shrink-0">Legales ({legales.length})</TabsTrigger>
          <TabsTrigger value="telegram" data-testid="tab-telegram" className="shrink-0">Telegram</TabsTrigger>
          <TabsTrigger value="backups" data-testid="tab-backups" className="shrink-0">Backups</TabsTrigger>
          <TabsTrigger value="diagnostico" data-testid="tab-diagnostico" className="shrink-0">Diagnóstico</TabsTrigger>
          <TabsTrigger value="migracion" data-testid="tab-migracion" className="shrink-0">Migración</TabsTrigger>
          <TabsTrigger value="backfill" data-testid="tab-backfill" className="shrink-0">Backfill IA</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding" className="shrink-0">Onboarding{obClics ? ` (${obClics.total_clics})` : ""}</TabsTrigger>
          <TabsTrigger value="reactivacion" data-testid="tab-reactivacion" className="shrink-0">Reactivación</TabsTrigger>
          <TabsTrigger value="reservas" data-testid="tab-reservas" className="shrink-0" onClick={cargarReservas}>Reservas{reservas ? ` (${reservas.total})` : ""}</TabsTrigger>
          <TabsTrigger value="reanalisis" data-testid="tab-reanalisis" className="shrink-0" onClick={cargarTruncamiento}>Reanálisis</TabsTrigger>
        </TabsList>

        <TabsContent value="trials" className="mt-4">
          <TrialsTab />
        </TabsContent>

        <TabsContent value="doctrina" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              {doctrina.map((d) => (
                <button key={d.clave} onClick={() => abrir(d)} data-testid={`doctrina-${d.clave}`}
                  className={`w-full text-left p-3 rounded-lg border ${sel === d.clave ? "border-[#FF5625] bg-[#FFF3EF]" : "border-[#E2E8F0] bg-white hover:bg-[#F7F8FA]"}`}>
                  <div className="flex items-center gap-2"><BookOpen size={14} className="text-[#FF5625]" /><span className="font-semibold text-sm text-[#0E2A47]">{d.titulo}</span></div>
                  <div className="text-[10px] font-mono text-[#0E2A47]/40 mt-1">{d.clave} · v{d.version}</div>
                </button>
              ))}
            </div>
            <div className="lg:col-span-2">
              {sel ? (
                <Card className="p-5 border-[#E2E8F0]">
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Título</Label>
                  <Input value={edit.titulo} onChange={(e) => setEdit({ ...edit, titulo: e.target.value })} data-testid="doctrina-titulo" className="mt-1.5 mb-3" />
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Contenido (markdown)</Label>
                  <Textarea value={edit.contenido} onChange={(e) => setEdit({ ...edit, contenido: e.target.value })} data-testid="doctrina-contenido" className="mt-1.5 min-h-[300px] font-mono text-xs" />
                  <Button onClick={guardar} data-testid="doctrina-guardar" className="mt-3 bg-[#0E2A47] hover:bg-[#163C65]"><Save size={15} className="mr-1" />Guardar (nueva versión)</Button>
                </Card>
              ) : <div className="text-[#0E2A47]/50 text-sm p-4">Selecciona una doctrina para editarla sin redeploy.</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="catalogo" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-3xl">
            <p className="text-sm text-[#0E2A47]/70 mb-3">Pega el JSON del catálogo IFS y elige el modo. En <b>reemplazar</b>, los requisitos ausentes del payload pasan a <b>retirado</b> (no se borran).</p>
            <div className="flex items-center gap-3 mb-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Modo</Label>
              <Select value={modo} onValueChange={setModo}>
                <SelectTrigger data-testid="import-modo" className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="upsert">upsert (por defecto)</SelectItem><SelectItem value="reemplazar">reemplazar</SelectItem></SelectContent>
              </Select>
            </div>
            <Textarea value={jsonText} onChange={(e) => setJsonText(e.target.value)} data-testid="import-json" placeholder='{"norma":"IFS_V8","requisitos":[...]}' className="min-h-[200px] font-mono text-xs" />
            <Button onClick={importar} data-testid="import-btn" className="mt-3 bg-[#FF5625] hover:bg-[#E0451A]"><Upload size={15} className="mr-1" />Importar catálogo</Button>
            {result && (
              <div className="mt-4 p-4 bg-[#F7F8FA] rounded-lg text-sm" data-testid="import-result">
                <div className="text-[#0E2A47]"><b>{result.importados}</b> importados · <b>{result.activos}</b> activos · <b>{result.retirados}</b> retirados</div>
                <div className="mt-2 flex flex-wrap gap-1">{result.kos.map((k) => <Badge key={k} className="bg-[#0E2A47] hover:bg-[#0E2A47] font-mono text-[10px]">KO {k}</Badge>)}</div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="etiquetas" className="mt-4">
          <Card className="p-5 border-[#E2E8F0]" data-testid="etiquetas-admin-panel">
            <p className="text-sm text-[#0E2A47]/70 mb-3">Registro de impresión de etiquetas (auditoría inmutable).</p>
            <div className="space-y-1">
              {impresiones.length === 0 && <div className="text-sm text-[#0E2A47]/40">Sin impresiones registradas.</div>}
              {impresiones.map((i) => (
                <div key={i.id} className="flex items-center justify-between text-sm border-b border-[#E2E8F0] py-2" data-testid={`admin-impresion-${i.id}`}>
                  <span className="text-[#0E2A47]">
                    <Badge variant="outline" className="text-[10px] mr-2">{i.tipo}</Badge>
                    {i.lote_codigo ? <span className="font-mono">{i.lote_codigo}</span> : <span className="italic text-[#0E2A47]/60">{i.contenido?.titulo || "libre"}</span>}
                    {i.bloqueado && <Badge className="bg-[#EF4444] hover:bg-[#EF4444] text-[10px] ml-2 text-white">BLOQUEADO</Badge>}
                  </span>
                  <span className="text-xs text-[#0E2A47]/50">{i.copias} ud · {i.formato === "a4_8" ? "A4×8" : "individual"} · {i.usuario_nombre} · {new Date(i.creado_en).toLocaleString("es-ES")}</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="empresas" className="mt-4">
          <div className="space-y-2" data-testid="empresas-panel">
            {tenants.map((t) => (
              <Card key={t.id} className="p-4 border-[#E2E8F0]" data-testid={`empresa-${t.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Building2 size={16} className="text-[#0E2A47]/40" />
                      <span className="font-semibold text-[#0E2A47]">{t.nombre}</span>
                      <Badge className={`${ESTADO_COLOR[t.estado] || "bg-[#94A3B8]"} hover:opacity-100 text-white text-[10px]`}>{t.estado || "—"}</Badge>
                      <Badge variant="outline" className="text-[10px]">{t.plan || "sin plan"}</Badge>
                    </div>
                    <div className="text-xs text-[#0E2A47]/50 mt-1">
                      {t.cif ? `CIF ${t.cif} · ` : ""}{t.usuarios} usuarios · {t.documentos} docs · niveles [{(t.niveles_activos || []).join(",")}]
                      {t.fecha_fin_trial ? ` · fin trial ${new Date(t.fecha_fin_trial).toLocaleDateString("es-ES")}` : ""}
                    </div>
                    <div className="text-xs mt-1" data-testid={`reactivacion-estado-${t.id}`}>
                      {t.reactivacion_enviada
                        ? <span className="text-green-700 font-medium">Reactivación enviada el {new Date(t.reactivacion_enviada).toLocaleDateString("es-ES")}</span>
                        : <span className="text-[#0E2A47]/40">Reactivación: sin enviar</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={activarSel[t.id] || ""} onValueChange={(v) => setActivarSel((s) => ({ ...s, [t.id]: v }))}>
                      <SelectTrigger className="h-9 w-40" data-testid={`activar-plan-select-${t.id}`}><SelectValue placeholder="Activar plan…" /></SelectTrigger>
                      <SelectContent className="bg-white">{planes.map((p) => <SelectItem key={p.codigo} value={p.codigo}>{p.nombre}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" onClick={() => activarTenant(t.id)} className="bg-[#10B981] hover:bg-[#0f9d74]" data-testid={`activar-btn-${t.id}`}><CheckCircle2 size={15} className="mr-1" />Activar</Button>
                    <Button size="sm" variant="outline" onClick={() => abrirExport(t)} className="text-[#0E2A47]" data-testid={`exportar-btn-${t.id}`}><Download size={15} className="mr-1" />Exportar datos</Button>
                    <Button size="sm" variant="outline" onClick={() => setBorrar(t)} className="text-[#EF4444] border-[#EF4444]/30 hover:bg-[#FEF2F2]" data-testid={`borrar-btn-${t.id}`}><Trash2 size={15} /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="planes" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="planes-panel">
            {planes.map((p) => {
              const e = planEdit[p.codigo] || { precio: String(p.precio), features: (p.features || []).join("\n") };
              return (
                <Card key={p.codigo} className="p-5 border-[#E2E8F0]" data-testid={`plan-admin-${p.codigo}`}>
                  <div className="flex items-center gap-2 mb-2"><CreditCard size={16} className="text-[#FF5625]" /><span className="font-black tracking-tighter text-[#0E2A47]">{p.nombre}</span>
                    <Badge variant={p.disponible ? "default" : "outline"} className="text-[10px] ml-auto">{p.disponible ? "enlace OK" : "sin enlace"}</Badge></div>
                  <div className="text-[10px] text-[#0E2A47]/40 mb-3">niveles [{(p.niveles || []).join(",")}] · env {p.pay_link_env}</div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Precio ({p.moneda}/{p.periodo})</Label>
                  <Input type="number" value={e.precio} onChange={(ev) => setPlanEdit((pe) => ({ ...pe, [p.codigo]: { ...e, precio: ev.target.value } }))} data-testid={`plan-precio-${p.codigo}`} className="mt-1.5 mb-3" />
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Features (una por línea)</Label>
                  <Textarea value={e.features} onChange={(ev) => setPlanEdit((pe) => ({ ...pe, [p.codigo]: { ...e, features: ev.target.value } }))} data-testid={`plan-features-${p.codigo}`} className="mt-1.5 min-h-[120px] text-xs" />
                  <Button onClick={() => guardarPlan(p.codigo)} className="mt-3 w-full bg-[#0E2A47] hover:bg-[#163C65]" data-testid={`plan-guardar-${p.codigo}`}><Save size={15} className="mr-1" />Guardar</Button>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="legales" className="mt-4">
          <div className="space-y-4" data-testid="legales-panel">
            <p className="text-sm text-[#0E2A47]/70">Edita el contenido legal y actívalo cuando tu asesor lo valide. Mientras esté <b>inactiva</b>, la ruta pública muestra "Disponible próximamente" y el registro no enlaza a un documento roto.</p>
            {legales.map((l) => {
              const e = legalEdit[l.clave] || { titulo: l.titulo, version: l.version, contenido: l.contenido };
              const setE = (patch) => setLegalEdit((pe) => ({ ...pe, [l.clave]: { ...e, ...patch } }));
              return (
                <Card key={l.clave} className="p-5 border-[#E2E8F0]" data-testid={`legal-admin-${l.clave}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} className="text-[#FF5625]" />
                      <span className="font-semibold text-[#0E2A47]">{l.titulo}</span>
                      <Badge className={`${l.activa ? "bg-[#10B981]" : "bg-[#94A3B8]"} hover:opacity-100 text-white text-[10px]`}>{l.activa ? "ACTIVA" : "inactiva"}</Badge>
                      <Badge variant="outline" className="text-[10px] font-mono">/legal/{l.clave}</Badge>
                    </div>
                    <Button size="sm" variant={l.activa ? "outline" : "default"} onClick={() => guardarLegal(l.clave, { activa: !l.activa })}
                      className={l.activa ? "text-[#EF4444] border-[#EF4444]/30" : "bg-[#10B981] hover:bg-[#0f9d74]"} data-testid={`legal-toggle-${l.clave}`}>
                      {l.activa ? "Desactivar" : "Activar (publicar)"}
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Título</Label>
                      <Input value={e.titulo} onChange={(ev) => setE({ titulo: ev.target.value })} data-testid={`legal-titulo-${l.clave}`} className="mt-1.5" /></div>
                    <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Versión</Label>
                      <Input value={e.version} onChange={(ev) => setE({ version: ev.target.value })} data-testid={`legal-version-${l.clave}`} className="mt-1.5" /></div>
                  </div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Contenido (markdown: **negrita**, "- " listas)</Label>
                  <Textarea value={e.contenido} onChange={(ev) => setE({ contenido: ev.target.value })} data-testid={`legal-contenido-${l.clave}`} className="mt-1.5 min-h-[280px] font-mono text-xs" />
                  <Button onClick={() => guardarLegal(l.clave)} className="mt-3 bg-[#0E2A47] hover:bg-[#163C65]" data-testid={`legal-guardar-${l.clave}`}><Save size={15} className="mr-1" />Guardar</Button>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="telegram" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-2xl" data-testid="webhook-panel">
            <div className="flex items-center gap-2 mb-1">
              <Send size={16} className="text-[#FF5625]" />
              <span className="font-black tracking-tighter text-[#0E2A47]">Webhook del bot @Auditready_bot</span>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">Autodiagnóstico: comprueba a qué URL entrega Telegram los mensajes del bot y si coincide con el dominio canónico (<code className="text-xs">BACKEND_EXTERNAL_URL</code>). Si no coincide, los códigos de vinculación fallarán.</p>

            {whLoading && <div className="text-sm text-[#0E2A47]/50 py-4">Consultando a Telegram…</div>}

            {!whLoading && webhook && !webhook.enabled && (
              <div className="text-sm text-[#B45309] bg-[#FFFBEB] border border-[#F59E0B]/30 rounded-lg p-3" data-testid="webhook-deshabilitado">
                <AlertTriangle size={15} className="inline mr-1" />Bot de Telegram no configurado (falta <code>TELEGRAM_BOT_TOKEN</code>).
              </div>
            )}

            {!whLoading && webhook && webhook.enabled && (
              <div className="space-y-4">
                <div className={`rounded-lg border p-3 ${webhook.coincide ? "border-[#10B981]/40 bg-[#F0FDF4]" : "border-[#EF4444]/40 bg-[#FEF2F2]"}`}>
                  <div className="flex items-center gap-2" data-testid="webhook-estado-badge" data-coincide={webhook.coincide}>
                    {webhook.coincide
                      ? <Badge className="bg-[#10B981] hover:bg-[#10B981] text-white"><CheckCircle2 size={13} className="mr-1" />Coincide</Badge>
                      : <Badge className="bg-[#EF4444] hover:bg-[#EF4444] text-white"><AlertTriangle size={13} className="mr-1" />No coincide</Badge>}
                    <span className="text-sm text-[#0E2A47]/70">{webhook.coincide ? "El webhook apunta al dominio correcto." : "El webhook NO apunta al dominio canónico. Pulsa «Re-registrar»."}</span>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#0E2A47]/50">URL actual (según Telegram)</div>
                    <div className="font-mono text-xs break-all text-[#0E2A47]" data-testid="webhook-url-actual">{webhook.url_actual || "— (sin webhook registrado)"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#0E2A47]/50">URL esperada (BACKEND_EXTERNAL_URL)</div>
                    <div className="font-mono text-xs break-all text-[#0E2A47]" data-testid="webhook-url-esperada">{webhook.url_esperada || "— (BACKEND_EXTERNAL_URL vacío)"}</div>
                  </div>
                  <div className="flex flex-wrap gap-4 pt-1 text-xs text-[#0E2A47]/60">
                    <span>Pendientes: <b className="text-[#0E2A47]">{webhook.pending_update_count}</b></span>
                    <span>Secret: {webhook.secret_configurado ? <b className="text-[#10B981]">configurado</b> : <b className="text-[#EF4444]">ausente</b>}</span>
                    <span>Updates: {(webhook.allowed_updates || []).join(", ") || "—"}</span>
                  </div>
                  {webhook.last_error_message && (
                    <div className="text-xs text-[#B91C1C] bg-[#FEF2F2] border border-[#EF4444]/30 rounded p-2" data-testid="webhook-last-error">
                      <AlertTriangle size={12} className="inline mr-1" />Último error de Telegram: {webhook.last_error_message}
                      {webhook.last_error_date ? ` (${new Date(webhook.last_error_date * 1000).toLocaleString("es-ES")})` : ""}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-2 border-t border-[#E2E8F0]">
                  <Button onClick={reregistrarWebhook} data-testid="webhook-reregistrar-btn" className="bg-[#FF5625] hover:bg-[#E0451A]"><Send size={15} className="mr-1" />Re-registrar webhook</Button>
                  <Button variant="outline" onClick={cargarWebhook} data-testid="webhook-refrescar-btn"><RefreshCw size={15} className="mr-1" />Refrescar</Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="backups" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-3xl" data-testid="backups-panel">
            <div className="flex items-center gap-2 mb-1">
              <Database size={16} className="text-[#FF5625]" />
              <span className="font-black tracking-tighter text-[#0E2A47]">Backups automáticos a Dropbox</span>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">Copia diaria (03:00 Europe/Madrid) de toda la base de datos y ficheros GridFS de todos los tenants. Retención: 7 diarios + 4 semanales + 3 mensuales. Un backup en el mismo contenedor no es un backup: todo sale fuera.</p>

            {backupData && !backupData.configurado && (
              <div className="text-sm text-[#B45309] bg-[#FFFBEB] border border-[#F59E0B]/30 rounded-lg p-3 mb-4" data-testid="backups-no-config">
                <AlertTriangle size={15} className="inline mr-1" />Backups <b>no configurados</b>. Faltan credenciales de Dropbox o <code>BACKUP_ENABLED=false</code>.
              </div>
            )}

            {backupData && backupData.configurado && (
              <div className="space-y-4">
                {backupData.ausente && (
                  <div className="rounded-lg border border-[#F59E0B]/50 bg-[#FFFBEB] p-3 flex items-start gap-2" data-testid="backups-alerta-ausente">
                    <AlertTriangle size={16} className="text-[#F59E0B] mt-0.5 shrink-0" />
                    <div className="text-sm text-[#B45309]">
                      <b>Sin backup correcto {backupData.horas_desde_ok != null ? `desde hace ${Math.round(backupData.horas_desde_ok)} h` : "(nunca completado)"}.</b> Umbral: {backupData.umbral_h} h. El backup podría estar fallando en silencio — se ha avisado por Telegram al superadmin. Revisa y ejecuta uno manual.
                    </div>
                  </div>
                )}
                <div className={`rounded-lg border p-3 ${backupData.ultimo?.estado === "ok" ? "border-[#10B981]/40 bg-[#F0FDF4]" : backupData.ultimo?.estado === "error" ? "border-[#EF4444]/40 bg-[#FEF2F2]" : "border-[#E2E8F0] bg-[#F8FAFC]"}`} data-testid="backups-ultimo">
                  {backupData.ultimo ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      {backupData.ultimo.estado === "ok"
                        ? <Badge className="bg-[#10B981] hover:bg-[#10B981] text-white"><CheckCircle2 size={13} className="mr-1" />Último OK</Badge>
                        : <Badge className="bg-[#EF4444] hover:bg-[#EF4444] text-white"><AlertTriangle size={13} className="mr-1" />Último ERROR</Badge>}
                      <span className="text-sm text-[#0E2A47]/70">{new Date(backupData.ultimo.fecha).toLocaleString("es-ES")}{backupData.ultimo.tamano_bytes ? ` · ${(backupData.ultimo.tamano_bytes / 1048576).toFixed(1)} MB` : ""}{backupData.ultimo.duracion_s != null ? ` · ${backupData.ultimo.duracion_s}s` : ""}</span>
                      {backupData.ultimo.mensaje && <span className="text-xs text-[#B91C1C] w-full">{backupData.ultimo.mensaje}</span>}
                    </div>
                  ) : <span className="text-sm text-[#0E2A47]/50">Aún no se ha ejecutado ningún backup.</span>}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button onClick={ejecutarBackup} disabled={backupBusy} data-testid="backup-run-btn" className="bg-[#FF5625] hover:bg-[#E0451A]">{backupBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Play size={15} className="mr-1" />}Ejecutar backup ahora</Button>
                  <Button variant="outline" onClick={probarRestore} disabled={restoreBusy} data-testid="backup-restore-btn">{restoreBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <RotateCcw size={15} className="mr-1" />}Probar restauración</Button>
                  <Button variant="outline" onClick={cargarBackups} data-testid="backup-refresh-btn"><RefreshCw size={15} className="mr-1" />Refrescar</Button>
                  <a href={backupData.carpeta_url} target="_blank" rel="noopener noreferrer" data-testid="backup-carpeta-link"><Button variant="outline" className="text-[#0E2A47]"><ExternalLink size={15} className="mr-1" />Carpeta Dropbox</Button></a>
                </div>

                {restoreRes && restoreRes.estado === "ok" && (
                  <div className="rounded-lg border border-[#10B981]/40 bg-[#F0FDF4] p-3 text-sm" data-testid="restore-resultado">
                    <div className="font-bold text-[#0E2A47] mb-1"><CheckCircle2 size={14} className="inline mr-1 text-[#10B981]" />Restauración probada · {restoreRes.integro ? "íntegra ✓" : "revisar ⚠️"}</div>
                    <div className="text-[#0E2A47]/70 text-xs">Backup: {restoreRes.backup} · {restoreRes.num_colecciones} colecciones · {restoreRes.total_documentos} documentos · GridFS {restoreRes.gridfs_files} ficheros / {restoreRes.gridfs_chunks} chunks</div>
                  </div>
                )}

                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#0E2A47]/50 mb-1">Últimos 15 backups</div>
                  <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="backups-lista">
                    {(backupData.lista || []).length === 0 && <div className="text-xs text-[#0E2A47]/40">Sin backups todavía.</div>}
                    {(backupData.lista || []).map((b) => (
                      <div key={b.id} className="flex items-center justify-between text-sm border-b border-[#E2E8F0] py-1.5" data-testid={`backup-item-${b.id}`}>
                        <span className="text-xs text-[#0E2A47]/70 truncate">{b.fichero}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-[#0E2A47]/50">{b.tamano_bytes ? `${(b.tamano_bytes / 1048576).toFixed(1)} MB` : "—"}</span>
                          {b.estado === "ok" ? <Badge variant="outline" className="text-[10px] text-[#10B981] border-[#10B981]/40">ok</Badge> : <Badge variant="outline" className="text-[10px] text-[#EF4444] border-[#EF4444]/40">error</Badge>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="diagnostico" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-3xl" data-testid="diagnostico-panel">
            <div className="flex items-center gap-2 mb-1">
              <Mail size={16} className="text-[#FF5625]" />
              <span className="font-black tracking-tighter text-[#0E2A47]">Diagnóstico de integraciones y emails</span>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">Comprueba el estado de Brevo (API key + plantillas) y del ciclo de emails transaccionales. El «corte Brevo» marca como descartados todos los emails pendientes actuales para que <b>solo las altas nuevas</b> reciban correos.</p>

            <div className="flex flex-wrap gap-2 mb-4">
              <Button onClick={comprobarIntegraciones} disabled={diagBusy} data-testid="diag-comprobar-btn" className="bg-[#0E2A47] hover:bg-[#163C65]">{diagBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <RefreshCw size={15} className="mr-1" />}Comprobar integraciones</Button>
              <Button onClick={() => setConfirmDescartar(true)} disabled={descartarBusy || !diag} data-testid="diag-descartar-btn" className="bg-[#EF4444] hover:bg-[#dc2626] text-white">{descartarBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <AlertTriangle size={15} className="mr-1" />}Descartar emails pendientes{pendientes != null ? ` (${pendientes})` : ""}</Button>
            </div>

            {diag && (
              <div className="space-y-3" data-testid="diag-resultado">
                <div className={`rounded-lg border p-3 ${diag.config?.brevo_api_key_configurada && diag.config?.plantillas?.bienvenida ? "border-[#10B981]/40 bg-[#F0FDF4]" : "border-[#F59E0B]/40 bg-[#FFFBEB]"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {diag.config?.brevo_api_key_configurada
                      ? <Badge className="bg-[#10B981] hover:bg-[#10B981] text-white"><CheckCircle2 size={13} className="mr-1" />Brevo API key</Badge>
                      : <Badge className="bg-[#EF4444] hover:bg-[#EF4444] text-white"><AlertTriangle size={13} className="mr-1" />Sin API key</Badge>}
                    {diag.config?.brevo_api_key_pista && <span className="font-mono text-xs text-[#0E2A47]/60">{diag.config.brevo_api_key_pista}</span>}
                    <Badge variant="outline" className="text-[10px]">Plantilla bienvenida: {diag.config?.plantilla_bienvenida_id || "—"}</Badge>
                  </div>
                  <div className="text-sm text-[#0E2A47]/70 mt-2">{diag.diagnostico}</div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(diag.config?.plantillas || {}).map(([k, v]) => (
                    <Badge key={k} variant="outline" className={`text-[10px] ${v ? "text-[#10B981] border-[#10B981]/40" : "text-[#EF4444] border-[#EF4444]/40"}`}>{k}: {v ? "ok" : "falta"}</Badge>
                  ))}
                  <Badge variant="outline" className={`text-[10px] ${diag.config?.telegram_configurado ? "text-[#10B981] border-[#10B981]/40" : "text-[#EF4444] border-[#EF4444]/40"}`}>telegram: {diag.config?.telegram_configurado ? "ok" : "falta"}</Badge>
                </div>

                <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3 text-sm">
                  <div className="text-[#0E2A47]"><b>{diag.emails_ciclo_total}</b> registros de emails en total</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(diag.emails_ciclo_por_estado || {}).map(([k, v]) => (
                      <Badge key={k} className="bg-[#0E2A47] hover:bg-[#0E2A47] text-white text-[10px]">{k}: {v}</Badge>
                    ))}
                  </div>
                </div>

                {descartarRes && (
                  <div className="rounded-lg border border-[#10B981]/40 bg-[#F0FDF4] p-3 text-sm" data-testid="diag-descartar-resultado">
                    <div className="font-bold text-[#0E2A47] mb-1"><CheckCircle2 size={14} className="inline mr-1 text-[#10B981]" />Corte Brevo aplicado</div>
                    <div className="text-[#0E2A47]/70 text-xs">{descartarRes.pendientes_descartados} pendientes descartados · {descartarRes.tombstones_creados} tombstones · {descartarRes.tenants_protegidos} tenants protegidos. {descartarRes.nota}</div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="migracion" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-4xl" data-testid="migracion-panel">
            <div className="flex items-center gap-2 mb-1">
              <Database size={16} className="text-[#FF5625]" />
              <span className="font-black tracking-tighter text-[#0E2A47]">Migración de scoring · documentos IA a vigente</span>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">
              Convierte los documentos subidos por IA que quedaron en <b>borrador</b> (bug anterior) a <b>vigente + confirmado</b> para que cuenten en la cobertura, y cierra los gaps que quedan cubiertos. Las <b>demos NO se tocan</b>. Ejecuta primero el <b>dry-run</b> (no cambia nada) y revisa el antes/después; luego pulsa <b>Aplicar</b>.
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              <Button onClick={migracionDryRun} disabled={migBusy} data-testid="migracion-dryrun-btn" className="bg-[#0E2A47] hover:bg-[#163C65]">
                {migBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Play size={15} className="mr-1" />}Ver migración (dry-run)
              </Button>
              {migDry && (
                <Button onClick={() => setConfirmMig(true)} disabled={migBusy} data-testid="migracion-aplicar-btn" className="bg-[#EF4444] hover:bg-[#dc2626] text-white">
                  {migBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <AlertTriangle size={15} className="mr-1" />}Aplicar migración
                </Button>
              )}
            </div>

            {migApplied && (
              <div className="rounded-lg border border-[#10B981]/40 bg-[#F0FDF4] p-3 mb-3 text-sm" data-testid="migracion-aplicada-resumen">
                <div className="font-bold text-[#0E2A47]"><CheckCircle2 size={14} className="inline mr-1 text-[#10B981]" />Migración aplicada</div>
                <div className="text-[#0E2A47]/70 text-xs mt-1">{migApplied.total_docs_migrados} documentos migrados a vigente · {migApplied.total_gaps_cerrados} gaps cerrados por cobertura · {migApplied.tenants} tenants revisados.</div>
              </div>
            )}

            {(migDry || migApplied) && (() => {
              const data = migApplied || migDry;
              const filas = (data.detalle || []).filter((r) => r.docs_ia_borrador > 0 || r.migrados > 0 || r.readiness_despues !== r.readiness_antes);
              const mostrar = filas.length ? filas : (data.detalle || []);
              return (
                <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]" data-testid="migracion-tabla">
                  <div className="text-xs text-[#0E2A47]/50 px-3 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    {data.modo} · {filas.length ? `${filas.length} tenants con documentos a migrar` : "ningún tenant con documentos IA en borrador"} (de {data.tenants} totales)
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[#0E2A47]/50 border-b border-[#E2E8F0]">
                        <th className="px-3 py-2">Empresa</th>
                        <th className="px-3 py-2">Docs IA borrador</th>
                        <th className="px-3 py-2">Readiness</th>
                        <th className="px-3 py-2">Cobertura</th>
                        <th className="px-3 py-2">Gaps cerrados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mostrar.map((r) => (
                        <tr key={r.tenant_id} className="border-b border-[#F1F5F9]" data-testid={`migracion-fila-${r.tenant_id}`}>
                          <td className="px-3 py-2 text-[#0E2A47]">{r.nombre || r.tenant_id}{r.demo && <Badge variant="outline" className="ml-2 text-[9px]">demo</Badge>}</td>
                          <td className="px-3 py-2">{r.docs_ia_borrador}</td>
                          <td className="px-3 py-2">
                            {r.readiness_antes}%{r.readiness_despues !== r.readiness_antes && <span className="text-[#10B981] font-semibold"> → {r.readiness_despues}%</span>}
                          </td>
                          <td className="px-3 py-2">
                            {r.cobertura_antes}%{r.cobertura_despues !== r.cobertura_antes && <span className="text-[#10B981] font-semibold"> → {r.cobertura_despues}%</span>}
                          </td>
                          <td className="px-3 py-2">{r.gaps_cerrados || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </Card>
        </TabsContent>

        <TabsContent value="backfill" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-4xl" data-testid="backfill-panel">
            <div className="flex items-center gap-2 mb-1">
              <Database size={16} className="text-[#FF5625]" />
              <span className="font-black tracking-tighter text-[#0E2A47]">Backfill de avisos IA · Bloque 1 (SOLO dry-run)</span>
              <Badge variant="outline" className="text-[9px] border-[#F59E0B] text-[#B45309]">TEMPORAL</Badge>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">
              Diagnóstico <b>sin cambios</b> del contrato "Cubierto con reservas + gate humano en KO". Muestra por empresa la cobertura <b>antes/después</b> del gate KO, cuántos requisitos pasan a <b>con reservas</b> (KO autoconfirmados por IA, pendientes de confirmación) y qué documentos IA no tienen <b>avisos_ia</b> guardados. Marca <b>Reanalizar con IA</b> para previsualizar los avisos que se regenerarían (no altera mapeos ni confianzas). <b>No hay botón de aplicar</b>: la escritura real se hará luego, previo backup de <code>documentos</code>.
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Button onClick={backfillDryRun} disabled={bfBusy} data-testid="backfill-dryrun-btn" className="bg-[#0E2A47] hover:bg-[#163C65]">
                {bfBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Play size={15} className="mr-1" />}Ver diagnóstico (dry-run)
              </Button>
              <label className="flex items-center gap-2 text-sm text-[#0E2A47]/80 cursor-pointer">
                <input type="checkbox" checked={bfReanalizar} onChange={(e) => setBfReanalizar(e.target.checked)} data-testid="backfill-reanalizar" className="h-4 w-4" />
                Reanalizar con IA (más lento)
              </label>
            </div>

            <div className="rounded-lg border border-[#EF4444]/30 bg-[#FEF2F2] p-3 mb-4">
              <div className="text-xs text-[#0E2A47]/70 mb-2">
                <b>Backfill REAL</b> (escribe <code>avisos_ia</code> en los docs IA, sin tocar mapeos/confianzas). Hace <b>backup</b> de <code>documentos</code> antes. ⚠️ Cambia cifras visibles: los docs con avisos pasan a <b>con reservas</b>. Se lanza manualmente.
              </div>
              <Button onClick={() => setApplyOpen(true)} data-testid="backfill-aplicar-btn" className="bg-[#EF4444] hover:bg-[#DC2626]">
                Aplicar backfill (escribe avisos)
              </Button>
              {applyRes && (
                <div className="text-xs text-[#15803D] mt-2" data-testid="backfill-aplicado">
                  Aplicado: {applyRes.docs_actualizados} docs · backup <code>{applyRes.backup_coleccion}</code>
                </div>
              )}
            </div>

            {bfDry && (
              <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]" data-testid="backfill-tabla">
                <div className="text-xs text-[#0E2A47]/50 px-3 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  {bfDry.modo} · {bfDry.tenants_con_impacto} empresas con impacto (de {bfDry.tenants_totales}) · {bfDry.total_docs_sin_avisos_ia} docs IA sin avisos guardados
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#0E2A47]/50 border-b border-[#E2E8F0]">
                      <th className="px-3 py-2">Empresa</th>
                      <th className="px-3 py-2">Norma</th>
                      <th className="px-3 py-2">Cobertura antes→después</th>
                      <th className="px-3 py-2">→ Con reservas</th>
                      <th className="px-3 py-2">Docs IA (sin avisos)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bfDry.detalle || []).map((r) => (
                      <tr key={r.tenant_id} className="border-b border-[#F1F5F9]" data-testid={`backfill-fila-${r.tenant_id}`}>
                        <td className="px-3 py-2 text-[#0E2A47]">{r.nombre || r.tenant_id}{r.demo && <Badge variant="outline" className="ml-2 text-[9px]">demo</Badge>}</td>
                        <td className="px-3 py-2 text-xs">{r.norma}</td>
                        <td className="px-3 py-2">
                          {r.cobertura_antes}%{r.cobertura_despues !== r.cobertura_antes && <span className="text-[#F59E0B] font-semibold"> → {r.cobertura_despues}%</span>}
                        </td>
                        <td className="px-3 py-2" title={(r.req_a_con_reservas_detalle || []).join(", ")}>{r.req_a_con_reservas || "—"}</td>
                        <td className="px-3 py-2">{r.docs_ia} <span className="text-[#F59E0B]">({r.docs_sin_avisos_ia})</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(bfDry.detalle || []).some((r) => (r.preview_avisos || []).length > 0) && (
                  <div className="p-3 border-t border-[#E2E8F0] text-xs" data-testid="backfill-preview-avisos">
                    <div className="font-bold text-[#0E2A47] mb-1">Previsualización de avisos a regenerar</div>
                    {(bfDry.detalle || []).flatMap((r) => (r.preview_avisos || []).map((p) => (
                      <div key={p.documento_id} className="mb-1 text-[#0E2A47]/70">
                        <b>{p.titulo}</b>: {(p.avisos || []).map((a) => a.tipo).join(", ") || "sin avisos"}
                      </div>
                    )))}
                  </div>
                )}
              </div>
            )}
          </Card>

          <Card className="p-5 border-[#E2E8F0] max-w-4xl mt-4" data-testid="huerfanos-panel">
            <div className="flex items-center gap-2 mb-1">
              <Database size={16} className="text-[#EF4444]" />
              <span className="font-black tracking-tighter text-[#0E2A47]">Auditoría de códigos huérfanos (SOLO lectura)</span>
              <Badge variant="outline" className="text-[9px] border-[#F59E0B] text-[#B45309]">TEMPORAL</Badge>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">
              Cuenta los mapeos a <b>códigos que no existen en el catálogo de la norma del tenant</b> (evidencia que la app ignora en la cobertura). Por tenant y por norma. <b>No toca datos.</b> Si todos los afectados son <b>demo</b>, es dato mal sembrado; si hay <b>tenants reales</b>, es bloqueante.
            </p>
            <Button onClick={auditarHuerfanos} disabled={huerfBusy} data-testid="huerfanos-btn" className="bg-[#0E2A47] hover:bg-[#163C65] mb-4">
              {huerfBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Play size={15} className="mr-1" />}Contar huérfanos
            </Button>

            {huerf && (
              <div className="space-y-3" data-testid="huerfanos-resultado">
                <div className={`rounded-lg px-3 py-2 text-sm font-semibold ${huerf.tenants_reales_afectados > 0 ? "bg-[#FEF2F2] text-[#EF4444] border border-[#EF4444]/30" : "bg-[#F0FDF4] text-[#15803D] border border-[#22C55E]/30"}`} data-testid="huerfanos-veredicto">
                  {huerf.veredicto} · {huerf.tenants_afectados} tenant(s) afectado(s), {huerf.tenants_reales_afectados} real(es)
                </div>
                {mrej && (
                  <div className="text-xs text-[#0E2A47]/70" data-testid="mapeos-rechazados-contador">
                    Mapeos rechazados por la validación de escritura (histórico): <b>{mrej.total}</b>
                    {Object.keys(mrej.por_norma || {}).length > 0 && " · " + Object.entries(mrej.por_norma).map(([n, o]) => `${n}: ${Object.values(o).reduce((a, b) => a + b, 0)}`).join(" · ")}
                  </div>
                )}
                <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                  <div className="text-xs text-[#0E2A47]/50 px-3 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0]">Por norma (mapeos huérfanos / mapeos totales)</div>
                  <div className="px-3 py-2 text-sm flex flex-wrap gap-x-6 gap-y-1">
                    {Object.entries(huerf.por_norma || {}).map(([n, v]) => (
                      <span key={n}><b>{n}</b>: <span className={v.orphan_mappings > 0 ? "text-[#EF4444]" : "text-[#15803D]"}>{v.orphan_mappings}</span> / {v.total_mappings}</span>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[#0E2A47]/50 border-b border-[#E2E8F0]">
                        <th className="px-3 py-2">Empresa</th><th className="px-3 py-2">Norma</th><th className="px-3 py-2">Huérfanos</th><th className="px-3 py-2">Códigos (norma donde sí existen)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(huerf.por_tenant || []).map((r) => (
                        <tr key={r.tenant_id} className="border-b border-[#F1F5F9]" data-testid={`huerfano-fila-${r.tenant_id}`}>
                          <td className="px-3 py-2 text-[#0E2A47]">{r.nombre}{r.demo && <Badge variant="outline" className="ml-2 text-[9px]">demo</Badge>}</td>
                          <td className="px-3 py-2 text-xs">{r.norma}</td>
                          <td className="px-3 py-2">{r.orphan_mappings} <span className="text-[#0E2A47]/40">({r.n_codigos_huerfanos} cód.)</span></td>
                          <td className="px-3 py-2 text-xs text-[#0E2A47]/70">{Object.entries(r.codigos || {}).map(([c, ns]) => `${c}${ns.length ? ` (→${ns.join("/")})` : " (inexistente)"}`).join(", ")}</td>
                        </tr>
                      ))}
                      {(huerf.por_tenant || []).length === 0 && (
                        <tr><td colSpan={4} className="px-3 py-3 text-center text-[#15803D]">Sin huérfanos. Todo el mapeo es válido.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-5 border-[#E2E8F0] max-w-5xl mt-4" data-testid="kpis-dryrun-panel">
            <div className="flex items-center gap-2 mb-1">
              <Database size={16} className="text-[#0E2A47]" />
              <span className="font-black tracking-tighter text-[#0E2A47]">Dry-run KPIs · enlazar gaps a cobertura (SOLO lectura)</span>
              <Badge variant="outline" className="text-[9px] border-[#F59E0B] text-[#B45309]">TEMPORAL</Badge>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">
              Antes/después por tenant si "gaps" pasa a leerse de la cobertura. Incluye <b>KO pendientes</b> por tenant (para decidir el color del badge) y el impacto en <b>readiness</b>. <b>No aplica nada.</b>
            </p>
            <Button onClick={kpisDryRun} disabled={kdryBusy} data-testid="kpis-dryrun-btn" className="bg-[#0E2A47] hover:bg-[#163C65] mb-4">
              {kdryBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Play size={15} className="mr-1" />}Ver dry-run de KPIs
            </Button>
            {kdry && (
              <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]" data-testid="kpis-dryrun-tabla">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#0E2A47]/50 border-b border-[#E2E8F0]">
                      <th className="px-2 py-2">Empresa</th><th className="px-2 py-2">Norma</th><th className="px-2 py-2">Cob%</th>
                      <th className="px-2 py-2">Reservas</th><th className="px-2 py-2">KO pend.</th><th className="px-2 py-2">KO s/ev.</th>
                      <th className="px-2 py-2">Gaps abiertos</th><th className="px-2 py-2">Readiness</th><th className="px-2 py-2">Badge correcto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(kdry.filas || []).map((f) => (
                      <tr key={f.tenant_id} className="border-b border-[#F1F5F9]" data-testid={`kpi-fila-${f.tenant_id}`}>
                        <td className="px-2 py-2 text-[#0E2A47]">{f.nombre}{f.demo && <Badge variant="outline" className="ml-1 text-[9px]">demo</Badge>}</td>
                        <td className="px-2 py-2 text-xs">{f.norma}</td>
                        <td className="px-2 py-2">{f.cobertura_pct}%</td>
                        <td className="px-2 py-2">{f.reservas}</td>
                        <td className="px-2 py-2 font-semibold text-[#B45309]">{f.ko_pendientes}</td>
                        <td className="px-2 py-2 font-semibold text-[#EF4444]">{f.ko_sin_evidencia}</td>
                        <td className="px-2 py-2">{f.gaps_abiertos_actual} → <b>{f.gaps_abiertos_nuevo}</b></td>
                        <td className="px-2 py-2">{f.readiness_actual}% → <b className={f.readiness_si_ko_penalizado !== f.readiness_actual ? "text-[#EF4444]" : ""}>{f.readiness_si_ko_penalizado}%</b></td>
                        <td className="px-2 py-2 text-xs">{f.badge_correcto}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-3xl" data-testid="onboarding-panel">
            <div className="flex items-center gap-2 mb-1">
              <Send size={16} className="text-[#FF5625]" />
              <span className="font-black tracking-tighter text-[#0E2A47]">Puesta en marcha asistida · clics por empresa</span>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">
              Cuántas veces se ha pulsado "Prefiero que me lo monten" / "Reserva 15 minutos" (evento <code>clic_onboarding_asistido</code>). Si nadie lo pulsa, el problema es otro.
            </p>
            {!obClics || obClics.filas.length === 0 ? (
              <div className="text-sm text-[#0E2A47]/40" data-testid="onboarding-vacio">Aún no hay clics registrados.</div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                <div className="text-xs text-[#0E2A47]/50 px-3 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  {obClics.total_clics} clics · {obClics.tenants_con_clic} empresas
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#0E2A47]/50 border-b border-[#E2E8F0]">
                      <th className="px-3 py-2">Empresa</th><th className="px-3 py-2">Clics</th><th className="px-3 py-2">Último clic</th>
                    </tr>
                  </thead>
                  <tbody>
                    {obClics.filas.map((f) => (
                      <tr key={f.tenant_id} className="border-b border-[#F1F5F9]" data-testid={`onboarding-fila-${f.tenant_id}`}>
                        <td className="px-3 py-2 text-[#0E2A47]">{f.nombre}</td>
                        <td className="px-3 py-2 font-semibold text-[#FF5625]">{f.total}</td>
                        <td className="px-3 py-2 text-xs text-[#0E2A47]/60">{f.ultima_fecha ? new Date(f.ultima_fecha).toLocaleString("es-ES") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="reactivacion" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-5xl" data-testid="reactivacion-panel">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <Send size={16} className="text-[#FF5625]" />
                <span className="font-black tracking-tighter text-[#0E2A47]">Reactivación de trials · plantilla 26</span>
              </div>
              <Button onClick={cargarReact} disabled={reactBusy} variant="outline" size="sm" data-testid="reactivacion-preview-btn">
                {reactBusy ? "Cargando…" : "Generar vista previa (dry-run)"}
              </Button>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">
              Vista previa obligatoria. Nada se envía desde aquí. Exclusiones automáticas: tenants de prueba, con 1+ documentos, email no verificado/baja/rebotado, o que ya recibieron la 26.
            </p>

            {!react ? (
              <div className="text-sm text-[#0E2A47]/40" data-testid="reactivacion-vacio">Pulsa "Generar vista previa" para ver el dry-run.</div>
            ) : (
              <>
                <div className="text-xs text-[#0E2A47]/60 mb-2">
                  {react.total} tenants · <span className="text-green-700 font-semibold">{react.incluidos} incluidos</span> · <span className="text-[#0E2A47]/60">{react.ya_enviados || 0} ya enviados</span> · <span className="text-[#0E2A47]/50">{react.excluidos} excluidos</span>
                </div>
                <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[#0E2A47]/50 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                        <th className="px-3 py-2">Empresa</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Norma</th>
                        <th className="px-3 py-2">Docs</th><th className="px-3 py-2">Alta</th><th className="px-3 py-2">Reactivación enviada</th><th className="px-3 py-2">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {react.filas.map((f) => (
                        <tr key={f.tenant_id} className="border-b border-[#F1F5F9]" data-testid={`reactivacion-fila-${f.tenant_id}`}>
                          <td className="px-3 py-2 text-[#0E2A47]">{f.empresa}</td>
                          <td className="px-3 py-2 text-xs text-[#0E2A47]/60">{f.email || "—"}</td>
                          <td className="px-3 py-2 text-xs text-[#0E2A47]/60">{f.norma || "—"}</td>
                          <td className="px-3 py-2">{f.documentos}</td>
                          <td className="px-3 py-2 text-xs text-[#0E2A47]/60">{f.fecha_alta ? new Date(f.fecha_alta).toLocaleDateString("es-ES") : "—"}</td>
                          <td className="px-3 py-2 text-xs">{f.reactivacion_enviada ? <span className="text-green-700 font-medium">{new Date(f.reactivacion_enviada).toLocaleDateString("es-ES")}</span> : <span className="text-[#0E2A47]/40">—</span>}</td>
                          <td className="px-3 py-2">
                            {f.incluido
                              ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-800">INCLUIDO</span>
                              : <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#F1F5F9] text-[#0E2A47]/60" title={f.motivo}>EXCLUIDO</span>}
                            <div className="text-[11px] text-[#0E2A47]/50 mt-0.5">{f.motivo}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 p-4 rounded-lg border border-[#FFD9C9] bg-[#FFF7F3]" data-testid="reactivacion-envio">
                  <p className="text-sm font-semibold text-[#B8451F] mb-2">Envío real ({react.incluidos} destinatarios)</p>
                  <p className="text-xs text-[#0E2A47]/60 mb-3">Escribe <code>ENVIAR REACTIVACION</code> para habilitar el botón. Idempotente: no reenvía a quien ya la recibió.</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <Input value={reactConf} onChange={(e) => setReactConf(e.target.value)} placeholder="ENVIAR REACTIVACION"
                      className="max-w-xs" data-testid="reactivacion-confirm-input" />
                    <Button onClick={enviarReact}
                      disabled={reactBusy || react.incluidos === 0 || reactConf.trim() !== "ENVIAR REACTIVACION"}
                      className="bg-[#FF5625] hover:bg-[#E0451A] text-white font-bold" data-testid="reactivacion-enviar-btn">
                      Enviar plantilla 26
                    </Button>
                  </div>
                  {reactRes && (
                    <div className="mt-3 text-sm" data-testid="reactivacion-resultado">
                      <span className="text-green-700 font-semibold">{reactRes.enviados} enviados</span> · <span className="text-red-700">{reactRes.fallidos} fallos</span>
                      <ul className="mt-1 text-xs text-[#0E2A47]/70 list-disc pl-5">
                        {reactRes.resultados.map((r) => (<li key={r.tenant_id}>{r.empresa}: <b>{r.estado}</b>{r.motivo ? ` — ${r.motivo}` : ""}</li>))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}
          </Card>

          <Card className="p-5 border-[#E2E8F0] max-w-5xl mt-4" data-testid="aperturas-panel">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-[#FF5625]" />
                <span className="font-black tracking-tighter text-[#0E2A47]">Conversión plantilla 26 · aperturas y clics (Brevo)</span>
              </div>
              <Button onClick={cargarAperturas} disabled={apBusy} variant="outline" size="sm" data-testid="aperturas-btn">
                {apBusy ? "Cargando…" : "Ver aperturas"}
              </Button>
            </div>
            {!aperturas ? (
              <div className="text-sm text-[#0E2A47]/40" data-testid="aperturas-vacio">Pulsa "Ver aperturas" para leer los eventos de Brevo de los envíos ya realizados.</div>
            ) : aperturas.enviados === 0 ? (
              <div className="text-sm text-[#0E2A47]/40">Aún no hay envíos de la plantilla 26.</div>
            ) : (
              <>
                <div className="text-xs text-[#0E2A47]/60 mb-2">
                  {aperturas.enviados} enviados · <span className="text-blue-700 font-semibold">{aperturas.abiertos} abiertos</span> · <span className="text-green-700 font-semibold">{aperturas.clics} clics</span>
                </div>
                <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[#0E2A47]/50 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                        <th className="px-3 py-2">Empresa</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Enviado</th><th className="px-3 py-2">Abierto</th><th className="px-3 py-2">Clic</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aperturas.filas.map((f) => (
                        <tr key={f.tenant_id} className="border-b border-[#F1F5F9]" data-testid={`apertura-fila-${f.tenant_id}`}>
                          <td className="px-3 py-2 text-[#0E2A47]">{f.empresa}</td>
                          <td className="px-3 py-2 text-xs text-[#0E2A47]/60">{f.email}</td>
                          <td className="px-3 py-2 text-xs text-[#0E2A47]/60">{f.enviado ? new Date(f.enviado).toLocaleDateString("es-ES") : "—"}</td>
                          <td className="px-3 py-2">{f.abierto ? <span className="text-blue-700 font-medium">Sí</span> : <span className="text-[#0E2A47]/40">—</span>}</td>
                          <td className="px-3 py-2">{f.clic ? <span className="text-green-700 font-medium">Sí</span> : <span className="text-[#0E2A47]/40">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="reservas" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-5xl" data-testid="reservas-panel">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <ExternalLink size={16} className="text-[#FF5625]" />
                <span className="font-black tracking-tighter text-[#0E2A47]">Reservas cal.com (15 min)</span>
              </div>
              <Button onClick={cargarReservas} disabled={reservasBusy} variant="outline" size="sm" data-testid="reservas-refresh-btn">
                {reservasBusy ? "Cargando…" : "Actualizar"}
              </Button>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">
              Reservas recibidas vía webhook de cal.com. También te llega un aviso al Telegram de AuditReady con cada reserva nueva.
            </p>
            {!reservas ? (
              <div className="text-sm text-[#0E2A47]/40" data-testid="reservas-vacio">Pulsa "Actualizar" para cargar las reservas.</div>
            ) : reservas.total === 0 ? (
              <div className="text-sm text-[#0E2A47]/40">Aún no hay reservas registradas.</div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#0E2A47]/50 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <th className="px-3 py-2">Nombre</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Teléfono</th>
                      <th className="px-3 py-2">Fecha reserva</th><th className="px-3 py-2">Notas</th><th className="px-3 py-2">Recibido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservas.filas.map((f) => (
                      <tr key={f.id} className="border-b border-[#F1F5F9]" data-testid={`reserva-fila-${f.id}`}>
                        <td className="px-3 py-2 text-[#0E2A47]">{f.nombre || "—"}</td>
                        <td className="px-3 py-2 text-xs text-[#0E2A47]/60">{f.email || "—"}</td>
                        <td className="px-3 py-2 text-xs text-[#0E2A47]/60">{f.telefono || "—"}</td>
                        <td className="px-3 py-2 text-xs text-[#0E2A47]/60">{f.fecha ? new Date(f.fecha).toLocaleString("es-ES") : "—"}</td>
                        <td className="px-3 py-2 text-xs text-[#0E2A47]/60 max-w-xs truncate" title={f.notas}>{f.notas || "—"}</td>
                        <td className="px-3 py-2 text-xs text-[#0E2A47]/50">{f.recibido_en ? new Date(f.recibido_en).toLocaleString("es-ES") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="reanalisis" className="mt-4">
          <Card className="p-5 border-[#E2E8F0] max-w-5xl" data-testid="reanalisis-panel">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <RefreshCw size={16} className="text-[#FF5625]" />
                <span className="font-black tracking-tighter text-[#0E2A47]">Auditoría de truncado y reanálisis</span>
              </div>
              <Button onClick={cargarTruncamiento} disabled={truncBusy} variant="outline" size="sm" data-testid="trunc-refresh-btn">
                {truncBusy ? "Auditando…" : "Ejecutar auditoría"}
              </Button>
            </div>
            <p className="text-sm text-[#0E2A47]/60 mb-4">
              Cuenta los documentos cuyo texto extraído supera el tope viejo (15.000 = mal mapeados) y el nuevo (60.000 = aún truncados). Reanaliza por tenant los documentos mapeados por IA, con dry-run previo.
            </p>
            {!trunc ? (
              <div className="text-sm text-[#0E2A47]/40" data-testid="trunc-vacio">Pulsa "Ejecutar auditoría".</div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]"><div className="text-[10px] uppercase text-[#0E2A47]/40">Docs analizados</div><b className="text-lg">{trunc.docs_analizados}</b></div>
                  <div className="p-3 rounded-lg bg-[#FFF7ED] border border-[#FF5625]/30"><div className="text-[10px] uppercase text-[#FF5625]">Mal mapeados (&gt;15k)</div><b className="text-lg">{trunc.afectados_por_truncado_viejo}</b><div className="text-[10px] text-[#0E2A47]/50">IA: {trunc.afectados_mapeados_por_ia}</div></div>
                  <div className="p-3 rounded-lg bg-[#FEF2F2] border border-[#EF4444]/30"><div className="text-[10px] uppercase text-[#EF4444]">Aún &gt;60k</div><b className="text-lg">{trunc.aun_superan_nuevo_umbral}</b></div>
                  <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]"><div className="text-[10px] uppercase text-[#0E2A47]/40">Doc más largo</div><b>{trunc.documento_mas_largo?.chars || 0}</b><div className="text-[10px] text-[#0E2A47]/50 truncate" title={trunc.documento_mas_largo?.titulo}>{trunc.documento_mas_largo?.titulo || "—"}</div></div>
                </div>
                <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] uppercase tracking-wider text-[#0E2A47]/50 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <th className="px-3 py-2">Tenant</th><th className="px-3 py-2">Total</th><th className="px-3 py-2">&gt;15k</th><th className="px-3 py-2">&gt;15k IA</th><th className="px-3 py-2">&gt;60k</th><th className="px-3 py-2 text-right">Reanálisis IA</th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(trunc.por_tenant).map(([tid, v]) => (
                        <tr key={tid} className="border-b border-[#F1F5F9]" data-testid={`trunc-fila-${tid}`}>
                          <td className="px-3 py-2 font-mono text-xs text-[#0E2A47]/70">{tid}</td>
                          <td className="px-3 py-2">{v.total}</td>
                          <td className="px-3 py-2 text-[#FF5625] font-bold">{v.supera_15000}</td>
                          <td className="px-3 py-2">{v.supera_15000_ia}</td>
                          <td className="px-3 py-2 text-[#EF4444]">{v.supera_60000}</td>
                          <td className="px-3 py-2 text-right">
                            <Button size="sm" variant="outline" disabled={reanBusy} onClick={() => reanTenantDry(tid)} data-testid={`rean-tenant-${tid}`}>Dry-run</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {reanDry && (
                  <div className="p-4 rounded-lg bg-[#FFF7ED] border border-[#FF5625]/30 space-y-2" data-testid="rean-tenant-dry">
                    <div className="font-bold text-[#0E2A47]">Dry-run · {reanTenant} · {reanDry.total} documentos</div>
                    <div className="max-h-56 overflow-y-auto text-xs space-y-1">
                      {reanDry.resultados.map((r) => (
                        <div key={r.documento_id} className="flex justify-between gap-2 border-b border-[#FF5625]/10 py-1">
                          <span className="truncate">{r.titulo}</span>
                          <span className="shrink-0 text-[#0E2A47]/60">{r.error ? `⚠ ${r.error}` : `req ${r.antes?.n_requisitos}→${r.despues?.n_requisitos} · conf ${r.antes?.confianza_media}→${r.despues?.confianza_media}% · ${r.despues?.idioma || "?"}`}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-[11px] text-[#B45309] flex items-start gap-1.5"><AlertTriangle size={13} className="mt-0.5" />Aplicar resetea las confirmaciones KO de estos documentos.</div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => { setReanDry(null); setReanTenant(null); }}>Cancelar</Button>
                      <Button size="sm" className="bg-[#FF5625] hover:bg-[#E64A1E]" disabled={reanBusy} onClick={reanTenantAplicar} data-testid="rean-tenant-aplicar">{reanBusy ? "Aplicando…" : `Aplicar a ${reanDry.total}`}</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="bg-white" data-testid="backfill-aplicar-dialog">
          <DialogHeader><DialogTitle className="text-[#EF4444]">Aplicar backfill de avisos IA</DialogTitle></DialogHeader>
          <p className="text-sm text-[#0E2A47]/70">Se hará un <b>backup</b> de la colección <code>documentos</code> y se escribirá <code>avisos_ia</code> en los documentos IA (sin tocar mapeos ni confianzas). <b>Esto baja la cobertura</b> de los clientes cuyos documentos tengan avisos. Escribe <b>APLICAR BACKFILL</b> para confirmar.</p>
          <Input value={applyTxt} onChange={(e) => setApplyTxt(e.target.value)} placeholder="APLICAR BACKFILL" data-testid="backfill-aplicar-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)} data-testid="backfill-aplicar-cancelar">Cancelar</Button>
            <Button onClick={aplicarBackfill} disabled={applyTxt !== "APLICAR BACKFILL" || applyBusy} data-testid="backfill-aplicar-confirmar" className="bg-[#EF4444] hover:bg-[#DC2626]">
              {applyBusy ? "Aplicando…" : "Aplicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmMig} onOpenChange={setConfirmMig}>
        <DialogContent className="bg-white" data-testid="migracion-dialog">
          <DialogHeader><DialogTitle className="text-[#EF4444]">Aplicar migración de scoring</DialogTitle></DialogHeader>
          <p className="text-sm text-[#0E2A47]/70">Se convertirán <b>{migDry?.detalle?.reduce((a, r) => a + r.docs_ia_borrador, 0) || 0}</b> documentos IA de borrador a <b>vigente</b> en {migDry?.detalle?.filter((r) => r.docs_ia_borrador > 0 && !r.demo).length || 0} empresas reales, y se cerrarán los gaps ya cubiertos. Las demos no se tocan. Esta acción modifica datos reales.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmMig(false)} data-testid="migracion-cancelar">Cancelar</Button>
            <Button disabled={migBusy} onClick={migracionAplicar} className="bg-[#EF4444] hover:bg-[#dc2626] text-white" data-testid="migracion-confirmar">{migBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <CheckCircle2 size={15} className="mr-1" />}Aplicar definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDescartar} onOpenChange={setConfirmDescartar}>
        <DialogContent className="bg-white" data-testid="descartar-dialog">
          <DialogHeader><DialogTitle className="text-[#EF4444]">Descartar emails pendientes</DialogTitle></DialogHeader>
          <p className="text-sm text-[#0E2A47]/70">Se marcarán como <b>descartados</b> {pendientes != null ? <><b>{pendientes}</b> emails pendientes</> : "todos los emails pendientes"} y se crearán tombstones para todos los tenants existentes. A partir de ahora <b>solo las altas nuevas</b> recibirán correos. Esta acción es irreversible.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDescartar(false)} data-testid="descartar-cancelar">Cancelar</Button>
            <Button disabled={descartarBusy} onClick={descartarPendientes} className="bg-[#EF4444] hover:bg-[#dc2626] text-white" data-testid="descartar-confirmar">{descartarBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <AlertTriangle size={15} className="mr-1" />}Descartar definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!exportDlg} onOpenChange={(o) => { if (!o) { setExportDlg(null); setExportaciones([]); } }}>
        <DialogContent className="bg-white max-w-lg" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0E2A47]">Exportar datos · {exportDlg?.nombre}</DialogTitle>
            <DialogDescription className="text-[#0E2A47]/60 text-sm">Genera un ZIP con documentos (por capítulo IFS), evidencias, informes y un XLSX de datos, más un README con aviso RGPD. Solo superadmin, bajo solicitud del cliente. La descarga usa un enlace de un solo uso y el fichero se borra tras descargarlo o a las 24 h.</DialogDescription>
          </DialogHeader>
          <Button onClick={generarExport} disabled={exportBusy} data-testid="export-generar-btn" className="bg-[#FF5625] hover:bg-[#E0451A]"><Download size={15} className="mr-1" />Generar nueva exportación</Button>
          <div className="space-y-1 max-h-72 overflow-y-auto mt-1" data-testid="export-lista">
            {exportaciones.length === 0 && <div className="text-xs text-[#0E2A47]/40">Sin exportaciones todavía.</div>}
            {exportaciones.map((e) => (
              <div key={e.id} className="flex items-center justify-between text-sm border-b border-[#E2E8F0] py-2" data-testid={`export-item-${e.id}`}>
                <div className="min-w-0">
                  <Badge variant="outline" className="text-[10px] mr-2">{e.estado}</Badge>
                  <span className="text-xs text-[#0E2A47]/60">{new Date(e.creado_en).toLocaleString("es-ES")}{e.tamano_bytes ? ` · ${(e.tamano_bytes / 1048576).toFixed(1)} MB` : ""}{e.num_documentos != null ? ` · ${e.num_documentos} docs` : ""}</span>
                </div>
                {e.estado === "completado"
                  ? <Button size="sm" className="h-7 text-xs bg-[#0E2A47] hover:bg-[#163C65]" onClick={() => descargarExport(e)} data-testid={`export-descargar-${e.id}`}><Download size={12} className="mr-1" />Descargar</Button>
                  : e.estado === "generando" ? <Loader2 size={14} className="animate-spin text-[#FF5625]" />
                    : <span className="text-xs text-[#0E2A47]/40">{e.estado === "descargado" ? "descargado" : e.estado === "caducado" ? "caducado (24h)" : e.estado}</span>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!borrar} onOpenChange={(o) => { if (!o) { setBorrar(null); setConfirmName(""); } }}>
        <DialogContent className="bg-white" data-testid="borrar-tenant-dialog">
          <DialogHeader><DialogTitle className="text-[#EF4444]">Eliminar empresa</DialogTitle></DialogHeader>
          <p className="text-sm text-[#0E2A47]/70">Esta acción es <b>irreversible</b> y borra todos los datos de <b>{borrar?.nombre}</b>. Para confirmar, escribe el nombre exacto de la empresa:</p>
          <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={borrar?.nombre} data-testid="borrar-confirm-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBorrar(null); setConfirmName(""); }} data-testid="borrar-cancelar">Cancelar</Button>
            <Button disabled={confirmName !== borrar?.nombre} onClick={eliminarTenant} className="bg-[#EF4444] hover:bg-[#dc2626] text-white" data-testid="borrar-confirmar"><Trash2 size={15} className="mr-1" />Eliminar definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
